import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import type { Role } from '../../lib/auth/types';

interface Props {
  children: ReactNode;
  /** Single role required, or array of roles where any one grants access. */
  requiredRole?: Role | Role[];
}

/**
 * Redirects anonymous users to /login, and authenticated-but-wrong-role
 * users to "/" so RoleHome can route them to a page they ARE allowed to
 * see. Hardcoding /dashboard as the bounce target was wrong for entrants
 * because they can't see /dashboard either — they'd get bounced into a
 * page that just bounces them again.
 */
export function ProtectedRoute({ children, requiredRole }: Props) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (requiredRole) {
    const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowed.some((r) => hasRole(r))) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}
